import React from "react";
import { Button, formatShortDate, Skeleton } from "@salestrak/ui";
import { useMemberLabelResolver } from "#app/lib/data/member-directory-provider";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { PanelBanner } from "./panel-banner";
import type { MemberDetail } from "./types";

/**
 * The invitation has not been accepted: say so, and when it went out. Resend
 * and cancel are manage.users actions — the same gate the server applies to
 * both mutations — and wait on the fetched row, which the cancel confirmation
 * names.
 */
export function InvitePendingBanner({
  userId,
  user,
  loading,
  canManage,
  onCancelInvite,
}: {
  userId: string | null;
  user: MemberDetail | null;
  /** The first fetch is in flight and nothing is cached for this member. */
  loading: boolean;
  canManage: boolean;
  onCancelInvite: (user: MemberDetail) => void;
}) {
  const resolveMemberLabel = useMemberLabelResolver();
  const { resendInvite } = useMemberMutations();

  // When it went out and who sent it. The date is the account's creation,
  // claimed only by an invite-created row (invitedById present), the same
  // rule the members table's Invited On column applies.
  const inviteSentOn =
    user?.invitedById && user.creationDate ? user.creationDate : null;
  const inviterName = user?.invitedById
    ? resolveMemberLabel(user.invitedById)
    : null;

  const [resending, setResending] = React.useState(false);
  const handleResendInvite = React.useCallback(async () => {
    if (!userId) return;
    setResending(true);
    try {
      await resendInvite(userId);
    } finally {
      setResending(false);
    }
  }, [userId, resendInvite]);

  return (
    <PanelBanner
      actions={
        canManage && user ? (
          <>
            <Button
              disabled={resending}
              onClick={() => void handleResendInvite()}
              size="sm"
              variant="outline"
            >
              Resend
            </Button>
            <Button onClick={() => onCancelInvite(user)} size="sm" variant="ghost">
              Cancel invite
            </Button>
          </>
        ) : undefined
      }
      description={
        loading ? (
          <Skeleton delayed height={12} width={160} />
        ) : inviteSentOn ? (
          `Sent ${formatShortDate(inviteSentOn)}${
            inviterName ? ` by ${inviterName}` : ""
          }`
        ) : (
          "Not accepted yet"
        )
      }
      icon="mail"
      layout="stack"
      title="Invitation pending"
      tone="warning"
    />
  );
}
